import { useEffect, useState, useSyncExternalStore } from 'react';
import { readSync, writeSync, hydrateFromNative } from './storage.js';
import * as music from '../utils/music.js';

const STORAGE_KEY = 'ballingo:v1';
const LEGACY_KEYS = ['croalingo:v1']; // migrate forward, in order

function migrateLegacyKeys() {
  if (readSync(STORAGE_KEY)) return;
  for (const k of LEGACY_KEYS) {
    const v = readSync(k);
    if (v) {
      writeSync(STORAGE_KEY, v);
      try { localStorage.removeItem(k); } catch {}
      return;
    }
  }
}
migrateLegacyKeys();
const MAX_HEARTS = 5;
const HEART_REGEN_MS = 1000 * 60 * 30; // one heart every 30 min

const defaultState = {
  // Identity / preferences
  sourceLang: 'sv', // 'sv' or 'en'
  dailyGoalXP: 30,

  // Gamification
  xp: 0,
  gems: 0,
  streak: 0,
  lastStreakDate: null, // YYYY-MM-DD
  hearts: MAX_HEARTS,
  lastHeartLossAt: null, // ms timestamp

  // Currently-active unit (skill). Defaults to the first incomplete skill.
  activeSkillId: null,

  // User-added phrases for personal practice. Each: { id, hr, meaning, createdAt }.
  customPhrases: [],

  // Daily XP
  todayKey: null,
  todayXP: 0,

  // Per-lesson completion counters: { [lessonId]: completionCount }
  lessonProgress: {},

  // Per-skill last completed lesson index (for unlocking next lesson)
  skillProgress: {}, // { [skillId]: { completed: 0 } }

  // Spaced repetition: { [itemKey]: { strength: 0..5, lastSeen: ms, wrong: count } }
  // itemKey = `${skillId}:${hr}` (Croatian word/phrase is unique-enough per skill)
  srs: {},

  // Lifetime totals
  totalLessons: 0,
  bestStreak: 0,

  // First-run experience. Defaults to false; on boot, anyone with prior
  // lesson activity is treated as already-onboarded so they don't see
  // the welcome screen retroactively. See shouldShowOnboarding() below.
  hasOnboarded: false,

  // Background music
  musicEnabled: true,
  musicVolume: 0.5,

  // Story mode (lands in Sprint 3 — schema is here so the load path merges
  // it forward without a separate migration)
  storyProgress: {},

  // Speed round
  speedRoundBest: 0,

  // Speaking exercises (mic). The actual Speak component / makeSpeak ships
  // in Sprint 3 — defaultState carries the prefs now so the Settings toggle
  // can persist across the future rollout.
  speakingEnabled: true,
  micDenied: false,
};

function loadState() {
  if (typeof window === 'undefined') return defaultState;
  try {
    const raw = readSync(STORAGE_KEY);
    if (!raw) return { ...defaultState };
    const parsed = JSON.parse(raw);
    return { ...defaultState, ...parsed };
  } catch {
    return { ...defaultState };
  }
}

let state = loadState();
const listeners = new Set();

// Push initial music prefs into the player.
music.setVolume(state.musicVolume);
music.setEnabled(state.musicEnabled);

function persist() {
  writeSync(STORAGE_KEY, JSON.stringify(state));
}

// On native, the WebView's localStorage may be empty even though the user
// has progress saved in UserDefaults/SharedPreferences (e.g. after the OS
// evicted the WebView storage). Hydrate from native on boot and re-emit.
if (typeof window !== 'undefined') {
  hydrateFromNative(STORAGE_KEY).then((rehydrated) => {
    if (rehydrated) {
      state = loadState();
      music.setVolume(state.musicVolume);
      music.setEnabled(state.musicEnabled);
      listeners.forEach((l) => l());
    }
  });
}

function notify() {
  listeners.forEach((l) => l());
}

function setState(updater) {
  const next = typeof updater === 'function' ? updater(state) : { ...state, ...updater };
  state = next;
  persist();
  notify();
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return state;
}

export function useProgress() {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

// ───── Helpers ────────────────────────────────────────────────────────────

function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function daysBetween(aKey, bKey) {
  if (!aKey || !bKey) return Infinity;
  const a = new Date(aKey + 'T00:00');
  const b = new Date(bKey + 'T00:00');
  return Math.round((b - a) / 86400000);
}

function rolloverDay(s) {
  const today = todayKey();
  if (s.todayKey !== today) {
    return { ...s, todayKey: today, todayXP: 0 };
  }
  return s;
}

function regenHearts(s) {
  if (s.hearts >= MAX_HEARTS || !s.lastHeartLossAt) {
    return s.hearts >= MAX_HEARTS ? { ...s, lastHeartLossAt: null } : s;
  }
  const elapsed = Date.now() - s.lastHeartLossAt;
  const gained = Math.floor(elapsed / HEART_REGEN_MS);
  if (gained <= 0) return s;
  const hearts = Math.min(MAX_HEARTS, s.hearts + gained);
  const remainder = elapsed - gained * HEART_REGEN_MS;
  return {
    ...s,
    hearts,
    lastHeartLossAt: hearts >= MAX_HEARTS ? null : Date.now() - remainder,
  };
}

// Public API ────────────────────────────────────────────────────────────────

export function setSourceLang(lang) {
  setState((s) => ({ ...s, sourceLang: lang }));
}

export function setDailyGoal(xp) {
  setState((s) => ({ ...s, dailyGoalXP: xp }));
}

export function setMusicEnabled(enabled) {
  const value = Boolean(enabled);
  music.setEnabled(value);
  setState((s) => ({ ...s, musicEnabled: value }));
}

export function setMusicVolume(volume) {
  const value = Math.max(0, Math.min(1, Number(volume) || 0));
  music.setVolume(value);
  setState((s) => ({ ...s, musicVolume: value }));
}

export function setSpeakingEnabled(enabled) {
  setState((s) => ({ ...s, speakingEnabled: Boolean(enabled) }));
}

export function markMicDenied() {
  setState((s) => ({ ...s, micDenied: true }));
}

// Awards XP through the same code path as completeLesson — speed round
// counts toward daily goal, total XP, and streak.
const SPEED_XP_PER_2_POINTS = 1;
const SPEED_XP_CAP = 30;
export function recordSpeedRound(score) {
  setState((s) => {
    const xpEarned = Math.min(SPEED_XP_CAP, Math.floor(Math.max(0, score) / 2) * SPEED_XP_PER_2_POINTS);
    const today = todayKey();
    let { streak, lastStreakDate, bestStreak } = s;
    if (lastStreakDate !== today) {
      const gap = daysBetween(lastStreakDate, today);
      streak = gap === 1 ? streak + 1 : 1;
      lastStreakDate = today;
      bestStreak = Math.max(bestStreak, streak);
    }
    return {
      ...s,
      xp: s.xp + xpEarned,
      todayXP: (s.todayKey === today ? s.todayXP : 0) + xpEarned,
      todayKey: today,
      streak,
      lastStreakDate,
      bestStreak,
      speedRoundBest: Math.max(s.speedRoundBest || 0, score),
    };
  });
}

export function completeStory(storyId, { xp = 25, gems = 2 } = {}) {
  setState((s) => {
    const today = todayKey();
    let { streak, lastStreakDate, bestStreak } = s;
    if (lastStreakDate !== today) {
      const gap = daysBetween(lastStreakDate, today);
      streak = gap === 1 ? streak + 1 : 1;
      lastStreakDate = today;
      bestStreak = Math.max(bestStreak, streak);
    }
    return {
      ...s,
      xp: s.xp + xp,
      gems: s.gems + gems,
      todayXP: (s.todayKey === today ? s.todayXP : 0) + xp,
      todayKey: today,
      streak,
      lastStreakDate,
      bestStreak,
      storyProgress: {
        ...(s.storyProgress || {}),
        [storyId]: { completed: true, completedAt: Date.now() },
      },
    };
  });
}

export function loseHeart() {
  setState((s) => {
    s = regenHearts(s);
    if (s.hearts <= 0) return s;
    return {
      ...s,
      hearts: s.hearts - 1,
      lastHeartLossAt: Date.now(),
    };
  });
}

export function refillHearts() {
  setState((s) => ({ ...s, hearts: MAX_HEARTS, lastHeartLossAt: null }));
}

export function tickHeartsRegen() {
  setState((s) => regenHearts(s));
}

// Update SRS strength for a vocabulary item.
// Correct: +1 strength (max 5). Incorrect: -1 strength (min 0).
export function recordAnswer({ skillId, hr, correct }) {
  const key = `${skillId}:${hr}`;
  setState((s) => {
    const prev = s.srs[key] || { strength: 0, lastSeen: 0, wrong: 0 };
    const strength = correct
      ? Math.min(5, prev.strength + 1)
      : Math.max(0, prev.strength - 1);
    return {
      ...s,
      srs: {
        ...s.srs,
        [key]: {
          strength,
          lastSeen: Date.now(),
          wrong: correct ? prev.wrong : prev.wrong + 1,
        },
      },
    };
  });
}

export function getItemStrength(skillId, hr) {
  return state.srs[`${skillId}:${hr}`]?.strength ?? 0;
}

// Returns the average strength (0..5) across a skill's items.
export function getSkillStrength(skill) {
  if (!skill.items.length) return 0;
  const total = skill.items.reduce(
    (sum, it) => sum + (state.srs[`${skill.id}:${it.hr}`]?.strength ?? 0),
    0
  );
  return total / skill.items.length;
}

export function completeLesson({ skillId, lessonId, lessonIndex, xpEarned, gemsEarned = 3 }) {
  setState((s) => {
    s = rolloverDay(s);
    const skillProg = s.skillProgress[skillId] || { completed: 0 };
    const newCompleted = Math.max(skillProg.completed, lessonIndex + 1);
    const today = todayKey();
    let { streak, lastStreakDate, bestStreak } = s;
    if (lastStreakDate !== today) {
      const gap = daysBetween(lastStreakDate, today);
      streak = gap === 1 ? streak + 1 : 1;
      lastStreakDate = today;
      bestStreak = Math.max(bestStreak, streak);
    }
    return {
      ...s,
      xp: s.xp + xpEarned,
      gems: s.gems + gemsEarned,
      todayXP: s.todayXP + xpEarned,
      streak,
      lastStreakDate,
      bestStreak,
      totalLessons: s.totalLessons + 1,
      lessonProgress: {
        ...s.lessonProgress,
        [lessonId]: (s.lessonProgress[lessonId] || 0) + 1,
      },
      skillProgress: {
        ...s.skillProgress,
        [skillId]: { completed: newCompleted },
      },
    };
  });
}

export function setActiveSkill(skillId) {
  setState((s) => ({ ...s, activeSkillId: skillId }));
}

export function completeOnboarding() {
  setState((s) => ({ ...s, hasOnboarded: true }));
}

// Returns true if the user hasn't been welcomed yet AND has no prior
// activity to indicate they've already used the app. Run from App.jsx
// on mount to decide whether to gate the main UI behind the onboarding.
export function shouldShowOnboarding() {
  return !state.hasOnboarded && state.totalLessons === 0;
}

// ─── Custom phrases ──────────────────────────────────────────────────────

function makePhraseId() {
  return `p_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export function addCustomPhrase({ hr, meaning }) {
  const trimmed = { hr: hr.trim(), meaning: meaning.trim() };
  if (!trimmed.hr || !trimmed.meaning) return null;
  const id = makePhraseId();
  setState((s) => ({
    ...s,
    customPhrases: [
      { id, hr: trimmed.hr, meaning: trimmed.meaning, createdAt: Date.now() },
      ...(s.customPhrases || []),
    ],
  }));
  return id;
}

export function updateCustomPhrase(id, { hr, meaning }) {
  setState((s) => ({
    ...s,
    customPhrases: (s.customPhrases || []).map((p) =>
      p.id === id ? { ...p, hr: hr.trim(), meaning: meaning.trim() } : p
    ),
  }));
}

export function removeCustomPhrase(id) {
  setState((s) => ({
    ...s,
    customPhrases: (s.customPhrases || []).filter((p) => p.id !== id),
  }));
}

// Reset everything — used by the "reset progress" button in settings
export function resetAll() {
  state = { ...defaultState };
  persist();
  notify();
}

// Hook that auto-rolls over the day and regenerates hearts on mount
export function useDailyTick() {
  const [, force] = useState(0);
  useEffect(() => {
    setState((s) => regenHearts(rolloverDay(s)));
    const t = setInterval(() => {
      setState((s) => regenHearts(rolloverDay(s)));
      force((n) => n + 1);
    }, 60_000);
    return () => clearInterval(t);
  }, []);
}

export const constants = { MAX_HEARTS, HEART_REGEN_MS };
